import { Inject, Options, Vue, Watch } from "vue-property-decorator";
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';
import store from '@/trust-admin-app/store';
import { TrustAPI } from '@/shared/modules/trust-api';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { orderBy } from 'lodash';
import AccessValidationMixin from '@/shared/mixins/access-validation';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { EventEmitter } from 'events';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import debounce from 'lodash/debounce';
import { InvestigationTypeView, InvestigationView, RoutineFilterRequest, RoutineView, RoutineStatus } from '@/shared/modules/typescript-trustapi-client/models';
import Tag from '@/shared/components/tag/index.vue';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import { AppInsights } from "@/trust-admin-app/appInsights";

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    Tag,
    DatePicker,
  },
  mixins: [
    AccessValidationMixin,
    FormatDateTimeMixin
  ]
})
export default class Routines extends Vue {
  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  private searchKeyword = '';
  private showActiveOnly = true;
  private noInvestigatorAssigned = false;
  private sortMethod = 'asc';
  private activeSortHeader = 2;
  private showCauseDrivenOnly = false;
  private isReplyRequired = false;

  private filterApplied = false;
  private srchDebounced: any = null;

  @Watch('searchKeyword')
  private onKeywordChange(): void {
    setTimeout(this.srchDebounced.cancel, 0);
    setTimeout(this.srchDebounced, 0);
  }

  private loadingContent = false;
  private errorLoadingContent = false;
  private loadingDataSet = false;
  private errorLoadingDataSet = false;
  private showAdvancedFilter = false;

  private closingDateRange = [null, null];

  private advanceFilter: any = {
    dateFrom: null,
    dateTo: null,
    address: null,
    investigationTypeId: null,
    routineStatusId: 1,
  }

  private tableHeadings = [
    { text: 'Practice (Trading name)', value: 'name', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Practice - branch ID', value: 'practiceBranchId', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Closing date', value: 'closingDate', sortMethod: 'desc', sortable: true, activeSort: true },
    { text: 'Investigation type', value: 'investigationTypeName', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Action', value: '', sortMethod: 'asc', sortable: false, activeSort: false },
  ];

  private investigationTypes: InvestigationTypeView[] = [];
  private routineStatuses: RoutineStatus[] = [];

  private routines: RoutineView[] = [];

  private routinesDisplay: any[] = [];

  @Watch('isReplyRequired')
  private onIsReplyRequiredChanged(): void {
    this.loadRoutines();
  }

  created(): void {
    this.preloadData();
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('reload-investigation-data', this.loadRoutines);
    this.srchDebounced = debounce(() => {
      this.loadRoutines();
    }, 1000);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-investigation-data', this.loadRoutines);
  }

  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetInvestigationTypesPost(),
        this.apiService.CodeApi?.apiV2CodeGetRoutineStatusesGet(),
        // this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetSupervisorsGet(),
      ]);
      this.investigationTypes = resp[0].data.data as InvestigationTypeView[];
      this.routineStatuses = resp[1].data.data as RoutineStatus[];
      this.loadRoutines();

    } catch (err) {
      this.errorLoadingDataSet = true;
      this.ai.trackException('Error, Unable to preload data: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to preload data');
    } finally {
      this.loadingDataSet = false;
    }
  }

  private async loadRoutines(): Promise<boolean> {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      const request: RoutineFilterRequest = {
        keyword: this.searchKeyword,
        dateFrom: null,
        dateTo: null,
        address: null,
        investigationTypeId: null,
        routineStatusId: 1,
        take: 100,
      };

      if (this.filterApplied) {
        request.dateFrom = this.closingDateRange[0];
        request.dateTo = this.closingDateRange[1];
        request.address = this.advanceFilter.address;
        request.investigationTypeId = this.advanceFilter.investigationTypeId;
        request.routineStatusId = this.advanceFilter.routineStatusId;
        request.take = 100;
      }

      const resp: any =
        await this.apiService.RoutineApi?.apiV2RoutineGetRoutinesPost(
          request
        );

      this.routines = resp.data.data as RoutineView[];

      this.routinesDisplay = [...this.routines];

      // convert to display table
      for (let i = 0; i < this.routines.length; i++) {
        this.routinesDisplay[i].name = this.routines[i].practice?.branchId ?
          this.routines[i].practice.name + ' (' + this.routines[i].practice.tradingName + ')' :
          this.routines[i].practice.name;
        this.routinesDisplay[i].practiceBranchId = this.routines[i].practice.branchId ?
          this.routines[i].practice.firmId + ' - ' + this.routines[i].practice.branchId :
          this.routines[i].practice.firmId.toString();
        this.routinesDisplay[i].investigationTypeName = this.routines[i].investigationType.name + ' (' + this.routines[i].investigationCategory.abbreviation + ')';
      }
      this.sortMethod = 'asc';
      this.sortTableColumn(2, true);

      return Promise.resolve(false);
    } catch (err) {
      this.errorLoadingContent = true;
      console.log(err);
      this.ai.trackException('Error, Unable to get routines list: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to get routines list');
      return Promise.reject(true);
    } finally {
      this.loadingContent = false;
    }
  }

  private applyFilter(): void {
    this.filterApplied = true;
    this.loadRoutines();
  }

  private resetFilter(): void {
    this.filterApplied = false;
    this.closingDateRange = [null, null];
    this.searchKeyword = '';
    this.advanceFilter = {
      dateFrom: null,
      dateTo: null,
      trustTeamMemberId: null,
      investigationTypeId: null,
      routineStatusId: 1,
    };
    this.loadRoutines();
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get usrAcct(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get RouteN(): typeof RouteN {
    return RouteN;
  }

  private sortTableColumn(index: number, sortable: boolean) {
    if (sortable) {
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        if (this.routinesDisplay.length > 0) {
          const nameSorter = index == 0 ? (c: any) => c.name.toLowerCase() : [this.tableHeadings[index].value, (c: any) => c.name.toLowerCase()];
          const orderByResults = orderBy(
            this.routinesDisplay,
            nameSorter,
            [this.sortMethod as any]
          );
          this.routinesDisplay = orderByResults;
        }
      }
    }
  }

  private addToInvestigation(r: RoutineView) {
    const _investigationId = r.investigationId;
    let newInvestigation: InvestigationView = {};
    this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationByIdInvestigationIdGet(_investigationId)
      .then(res => {
        newInvestigation = res.data.data;
        newInvestigation.id = 0;
        this.eventCtrl.emit('showAddEditInvestigationModal', newInvestigation, true, r.id);
      })
      .catch(err => {
        this.ai.trackException('Error, Unable to add investigation: ' + err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to add investigation:');
      });
  }

}
